import { apiService } from './api';

export interface Role {
  _id: string;
  schoolId: string;
  name: string;
  description?: string;
  permissions: Array<{
    _id: string;
    name: string;
    module: string;
    action: string;
    description?: string;
  }>;
  isSystemRole: boolean;
  // Full-access flag set on the auto-created School Admin role.
  isSchoolAdmin?: boolean;
  // Teacher-role flag — restricts the holder to their own class-teacher /
  // subject-teacher assignments. Auto-set on the system Teacher role only.
  isTeacher?: boolean;
  isActive: boolean;
  createdBy?: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoleData {
  name: string;
  description?: string;
  permissions?: string[]; // Array of permission IDs
}

export interface UpdateRoleData {
  name?: string;
  description?: string;
  permissions?: string[]; // Array of permission IDs
}

export const roleService = {
  // Get all users for school admin.
  // Teacher subject-teaching wiring lives in the Teaching Loads module
  // (/api/teaching-loads/*) and is NOT returned from this endpoint.
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    roleId?: string;
    isActive?: boolean;
  }) => {
    const response = await apiService.get<{
      users: any[];
      pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
    }>('/admin/users', params);
    return response.data;
  },

  // Get all roles for the school (full payload, populated permissions).
  // Requires `settings_roles.read` — used by the role-management form.
  getRoles: async () => {
    const response = await apiService.get<Role[]>('/admin/roles');
    return response.data;
  },

  // Catalog-tier list — id + name + flags only. Open to any authenticated
  // school user. Use this on filter dropdowns, role chips, leave-policy
  // assignment pickers — anywhere you only need the role's identity.
  getRolesCatalog: async () => {
    const response = await apiService.get<Pick<Role, '_id' | 'name' | 'description' | 'isSystemRole' | 'isTeacher' | 'isSchoolAdmin'>[]>('/roles');
    return response.data;
  },

  // Get role by ID
  getRoleById: async (roleId: string) => {
    const response = await apiService.get<Role>(`/admin/roles/${roleId}`);
    return response.data;
  },

  // Create new role
  createRole: async (data: CreateRoleData) => {
    const response = await apiService.post<Role>('/admin/roles', data);
    return response.data;
  },

  // Update role
  updateRole: async (roleId: string, data: UpdateRoleData) => {
    const response = await apiService.patch<Role>(`/admin/roles/${roleId}`, data);
    return response.data;
  },

  // Delete role
  deleteRole: async (roleId: string) => {
    const response = await apiService.delete(`/admin/roles/${roleId}`);
    return response.data;
  },

  // Get all available permissions (for role creation/editing)
  getAvailablePermissions: async () => {
    const response = await apiService.get<{
      permissions: Array<{
        _id: string;
        name: string;
        module: string;
        action: string;
        description?: string;
        category: string;
      }>;
      grouped: Record<string, any>;
    }>('/admin/permissions');
    return response.data;
  },

  // Create user with school-specific role (for school admins).
  // Identity only — name, email, phone, role, password. Subject wiring for
  // teachers is created separately via the Teaching Loads module.
  createUser: async (data: {
    name: string;
    email: string;
    password: string;
    phone?: string;
    roleId: string;
  }) => {
    const response = await apiService.post('/admin/users', data);
    return response.data;
  },

  // Update user with school-specific role (for school admins). Identity only.
  updateUser: async (userId: string, data: {
    name?: string;
    email?: string;
    phone?: string;
    roleId?: string;
    isActive?: boolean;
  }) => {
    const response = await apiService.patch(`/admin/users/${userId}`, data);
    return response.data;
  },
};

